/* eslint-disable consistent-return */
/**
 * @file 用户资产数据
 * @date 2021-07-03
 */

import { ls, ram } from '@sfe/bone-cache';
import { userapi, leverageApi, botApi } from '@/apis/spot';
import { userapi as ucapi } from '@/apis/api2';
import { optionapi } from '@/apis/unified';
import { getUserInfo } from '@/utils/userInfo';
import { toArray } from '@/utils/tools';
import { logout as logoutUserWebSocket, login as loginUserWebSocket } from '@/utils/userWebSocket';
import { error as errorToast } from '@/utils/toast';

const state = {
    /**
     * 资产数据列表，使用前请先根据 `user.userstate` 判断处理
     */
    balances: [],

    /**
     * 统一账户资产数据
     */
    unifiedBalances: {
        coinList: [],
    },

    /**
     * 全仓杠杆下，现货账户信息
     * 可用总额
     * 风险率/有效总资产/总欠款
     * 需要借贷数量
     */
    accountInfo: {
        needBorrow: 0, // 需要借贷数量
    },

    /**
     * 是否显示资产
     */
    show: ls.get('showAssets') === '1' || !ls.get('showAssets'),

    /**
     * 创新区额度，所有创新区币种
     */
    innovateQuotas: [],
    /**
     * 最大可转额度，使用前请先根据 `user.userstate` 判断处理
     */
    balanceLimit: {},
    fundsAsset: '', // 资金账户资产
};

const getters = {
    // 用户资产(现货账户和统一账户使用同一个map)，以 `tokenId` 为 key
    balanceMap(state, _, rootState, rootGetters) {
        if (rootState.user.userstate === 1) {
            const isUTA = rootGetters['user/isUTA'];
            const list = isUTA ? state.unifiedBalances?.coinList : state.balances;
            return list.reduce((data, item) => {
                data[item.tokenId] = {
                    ...item,
                    free: isUTA ? item.spotAvailableBalance : item.free,
                    transferLimit: state.balanceLimit[item.tokenId] || null,
                };
                return data;
            }, {});
        }
        return {};
    },
    // 用户额度
    quotaMap(state, _, rootState, rootGetters) {
        const isUTA = rootGetters['user/isUTA'];
        return state.innovateQuotas.reduce((data, item) => {
            // key 升级统一账户后为 基础币种，升级前为 币对
            const key = isUTA ? item.coin : item.symbolId;
            data[key] = item;
            return data;
        }, {});
    },
};

const mutations = {
    SET_BALANCES(state, payload) {
        state.balances = payload;
    },
    SET_UNIFIED_BALANCES(state, payload) {
        state.unifiedBalances = payload;
    },
    SET_BALCACES_LIMIT(state, payload) {
        const limitMap = {};
        if (!payload?.balance?.sCoin) return;
        limitMap[payload.balance.sCoin] = payload.balance.transferBalance;
        state.balanceLimit = limitMap;
    },
    SET_ACCOUNT_INFO(state, payload) {
        state.accountInfo = { ...state.accountInfo, ...payload };
    },
    SET_BALANCES_BY_TOKENID(state, payload) {
        toArray(payload).forEach((balance) => {
            const isNotNewCurrency = state.balances.some((item, index) => {
                if (item.tokenId === balance.tokenId) {
                    state.balances[index] = balance;
                    return true;
                }
                return false;
            });
            if (!isNotNewCurrency) state.balances.push(balance);
        });
    },
    SET_SHOW(state, isShow) {
        state.show = isShow;
        ls.set('showAssets', isShow ? '1' : '0');
    },
    SET_INNOVATE_QUOTAS(state, data) {
        state.innovateQuotas = data;
    },
    UPDATE_INNOVATE_QUOTAS(state, data) {
        const quotas = [];
        state.innovateQuotas.forEach((v) => {
            const amount = data.find((n) => n.symbolId === v.symbolId);
            if (amount?.availableQuota) {
                v.remainAmount = amount.availableQuota;
            }
            quotas.push(v);
        });
        state.innovateQuotas = quotas;
    },
    // 更新资金资产
    UPDATE_FUNDS_ASSET(state, payload) {
        state.fundsAsset = payload;
    },
};
const actions = {
    // get 方法获取用户资产
    async GET_ASSET({ dispatch, commit }) {
        const res = await userapi.getAsset();
        if (!res.success) {
            errorToast(res.message);
            return dispatch('LOGOUT');
        }
        commit('SET_BALANCES', res.result);
    },

    // get 方法获取用户预估可用资产(最大可转出额)
    async GET_AVAILABLE({ commit }, params) {
        const res = await ucapi.getCoinBalance(params);
        if (res.success) {
            commit('SET_BALCACES_LIMIT', res.result);
        } else {
            errorToast(res.message);
        }
    },

    // 全仓杠杆下 查询现货账户信息（风险率/有效总资产/总欠款）
    async GET_LEVERAGE_RISK_INFO({ commit }) {
        const res = await leverageApi.getRiskInfo();
        if (res.success) {
            const { result } = res;
            commit('SET_ACCOUNT_INFO', result);
        }
    },

    async INIT({ dispatch, commit, rootGetters }) {
        const userInfo = await getUserInfo();
        if (!userInfo) return dispatch('LOGOUT');
        const isUTA = rootGetters['user/isUTA'];
        // 获取资产显示状态
        dispatch('GET_SHOWASSETS');

        if (!isUTA) {
            // 获取用户现货账户资产
            dispatch('GET_ASSET');

            // 这里是订阅 WebSocket 资产网关的代码
            loginUserWebSocket({ isUTA }).then((ws) => {
                ws.sub({ topic: 'balance' }, (data) => {
                    if (data.code === 200 && data.data && data.data.length) {
                        commit('SET_BALANCES_BY_TOKENID', data.data);
                    }
                });
            });
        }
    },

    // 获取资产显示状态
    async GET_SHOWASSETS({ commit }) {
        const res = await ucapi.getPreferences({
            preference_keys: ['showAssets'],
        });

        // `showAssets` 字段的类型是 String ，哪怕是 '0'
        if (res.success && res.result?.preferences?.showAssets) {
            commit('SET_SHOW', res.result.preferences.showAssets === '1');
        }
    },

    // 设置资产显示状态
    async SET_SHOWASSETS({ commit }, isShow) {
        // 更新接口，这里不用同步，为了让页面操作更纵享丝滑
        ucapi.setPrefSetting({
            upsert_keys: {
                showAssets: isShow ? '1' : '0',
            },
        });

        commit('SET_SHOW', isShow);
    },

    // 退出登录后销毁用户数据
    // 1. 资产数据
    // 2. WebSocket 订阅
    async LOGOUT({ commit }) {
        commit('SET_BALANCES', []);
        commit('UPDATE_FUNDS_ASSET', '');

        // 这里是取消 WebSocket 资产网关的代码
        await logoutUserWebSocket();
    },

    // 获取用户创新区额度
    async GET_INNOVATION_QUOTA({ commit, dispatch }) {
        const { success, result } = await userapi.getInnovateQuota();
        if (!success) return;
        commit('SET_INNOVATE_QUOTAS', result);
        // 订阅创新区剩余额度
        dispatch('SUB_INNOVATE_QUOTA');
    },

    // 订阅创新区额度
    SUB_INNOVATE_QUOTA({ commit, dispatch }) {
        dispatch('UN_SUB_INNOVATE_QUOTA');
        loginUserWebSocket({ isUTA: false }).then((ws) => {
            const stop = ws.sub({ topic: 'innovation_position' }, ({ data }) => {
                if (data?.length) commit('UPDATE_INNOVATE_QUOTAS', data);
            });
            ram.set('innovate-quota-last', stop);
        });
    },

    // 取消订阅创新区额度
    UN_SUB_INNOVATE_QUOTA() {
        const unSub = ram.get('innovate-quota-last');
        if (unSub) unSub();
    },

    // 统一账户升级后获取创新区持仓限额
    async GET_INNOVATION_QUOTA_UTA({ commit }) {
        const { success, result } = await optionapi.getInnovateQuotaUta({ type: 2 });
        if (!success) return;
        commit('SET_INNOVATE_QUOTAS', result);
    },
    // 获取资金账户资产
    GET_FUNDS_ASSET({ commit }, coin) {
        botApi.getFundsAsset({ coin }).then((res) => {
            // eslint-disable-next-line camelcase
            if (res.success && res.result?.available_balance) {
                commit('UPDATE_FUNDS_ASSET', res.result.available_balance);
            }
        });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
